import {toAuthSession} from "@/adapters/auth.adapter";
import {PATHS} from "@/constants/routes";
import {loginService} from "@/services/auth.service";
import {useAuthStore} from "@/store/authStore";
import {useMutation} from "@tanstack/react-query";
import {useState} from "react";
import {useNavigate} from "react-router";
import {z} from "zod";

const loginSchema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
});

type LoginForm = z.infer<typeof loginSchema>;
type FormErrors = Partial<Record<keyof LoginForm, string>>;

export const useLogin = () => {
  const navigate = useNavigate();
  const login = useAuthStore(s => s.login);

  const [form, setForm] = useState<LoginForm>({username: "", password: ""});
  const [errors, setErrors] = useState<FormErrors>({});

  const mutation = useMutation({
    mutationKey: ["login"],
    mutationFn: (data: LoginForm) => loginService(data.username, data.password),
    onSuccess: response => {
      if (!response.data) return;
      const session = toAuthSession(response.data);
      login(session.accessToken, session.user, session.refreshToken);
      navigate(PATHS.DASHBOARD, {replace: true});
    },
  });

  const serverError = mutation.error?.message ?? "";

  const setField = (field: keyof LoginForm, value: string) => {
    setForm(prev => ({...prev, [field]: value}));
    if (errors[field]) setErrors(prev => ({...prev, [field]: undefined}));
    if (serverError) mutation.reset();
  };

  const submit = () => {
    const result = loginSchema.safeParse(form);
    if (!result.success) {
      const fieldErrors: FormErrors = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as keyof LoginForm;
        fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    mutation.mutate(result.data);
  };

  return {form, errors, serverError, loading: mutation.isPending, setField, submit};
};
